// src/App.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './hooks/useRedux';
import { getCurrentUser } from './store/slices/authSlice';
import { getCsrfToken, getToken } from './utils/sessionManager';
import { shouldShowProtectedRouteSpinner } from './utils/protectedRouteAuth';
import realtimeService from './services/realtimeService';

// Home pages
import HomePage from './pages/HomePage'; // Old homepage
import NewHomePage from './pages/NewHomePage'; // New enhanced homepage

// New public pages
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import ContactPage from './pages/ContactPage';

// Public, indexable marketplace pages (no authentication).
// These are ADDITIVE — every existing route keeps its current behaviour.
import StylistDirectoryPage from './pages/public/StylistDirectoryPage';
import PublicStylistProfilePage from './pages/public/PublicStylistProfilePage';
import ServiceHubPage from './pages/public/ServiceHubPage';
import LocationIndexPage from './pages/public/LocationIndexPage';
import StateLandingPage from './pages/public/StateLandingPage';
import CityLandingPage from './pages/public/CityLandingPage';
import CityServiceLandingPage from './pages/public/CityServiceLandingPage';

// Layout components
import AuthLayout from './components/layout/AuthLayout';
import DashboardLayout from './components/layout/DashboardLayout';
import AffiliateLayout from './components/layout/AffiliateLayout';
import GuestMarketplaceLayout from './components/layout/GuestMarketplaceLayout';
import BecomeStylistPage from './pages/BecomeStylistPage';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';

// Client pages
import ClientDashboardPage from './pages/client/DashboardPage';
import BrowseStylesPage from './pages/client/BrowseStylesPage';
import StylistDetailsPage from './pages/client/StylistDetailsPage';
import BookingPage from './pages/client/BookingPage';
import BookingSuccessPage from './pages/client/BookingSuccessPage';
import AppointmentsPage from './pages/client/AppointmentsPage';
import AppointmentDetailsPage from './pages/client/AppointmentDetailsPage';
import ProfilePage from './pages/client/ProfilePage';
import EditProfilePage from './pages/client/EditProfilePage';
import SecurityPage from './pages/shared/SecurityPage';
import SupportPage from './pages/shared/SupportPage';
import NotificationsPage from './pages/shared/NotificationsPage';
import ChatPage from './pages/shared/ChatPage';

// Admin pages
import SupportDashboardPage from './pages/admin/SupportDashboardPage';
import AffiliateManagementPage from './pages/admin/AffiliateManagementPage';
import DisputeManagementPage from './pages/admin/DisputeManagementPage';
import PayoutManagementPage from './pages/admin/PayoutManagementPage';
import AdminEmailVerificationPage from './pages/admin/EmailVerificationPage';
import StylistApprovalPage from './pages/admin/StylistApprovalPage';
import AuditTrailsPage from './pages/admin/AuditTrailsPage';
import QuickUserSearchPage from './pages/admin/QuickUserSearchPage';
import FeatureGatesPage from './pages/admin/FeatureGatesPage';
import SystemHealthPage from './pages/admin/SystemHealthPage';
import AdminActionCenterPage from './pages/admin/AdminActionCenterPage';
import AccountDeletionsPage from './pages/admin/AccountDeletionsPage';
import AccountDeletionDetailPage from './pages/admin/AccountDeletionDetailPage';

// Affiliate pages
import AffiliateRegisterPage from './pages/affiliate/RegisterPage';
import AffiliateLoginPage from './pages/affiliate/LoginPage';
import AffiliateDashboardPage from './pages/affiliate/DashboardPage';
import AffiliateCommissionsPage from './pages/affiliate/CommissionsPage';
import AffiliatePayoutsPage from './pages/affiliate/PayoutsPage';
import AffiliateMarketingMaterialsPage from './pages/affiliate/MarketingMaterialsPage';
import AffiliateProfilePage from './pages/affiliate/ProfilePage';
import AffiliateAnalyticsPage from './pages/affiliate/AnalyticsPage';
import AffiliateHelpPage from './pages/affiliate/HelpPage';

// Referral tracking
import ReferralTrackingPage from './pages/ReferralTrackingPage';

// Legal pages
import PrivacyPage from './pages/PrivacyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import TermsPage from './pages/TermsPage';
import ProviderAgreementPage from './pages/legal/ProviderAgreementPage';
import CancellationPolicyPage from './pages/legal/CancellationPolicyPage';
import CommunityStandardsPage from './pages/legal/CommunityStandardsPage';
import AffiliateTermsPage from './pages/legal/AffiliateTermsPage';

// Stylist pages
import StylistDashboardPage from './pages/stylist/DashboardPage';
import ServiceManagementPage from './pages/stylist/ServiceManagementPage';
import ServiceTemplatePage from './pages/stylist/ServiceTemplatePage';
import AddOnTemplatePage from './pages/stylist/AddOnTemplatePage';
import EditTemplatePage from './pages/stylist/EditTemplatePage';
import SchedulePage from './pages/stylist/SchedulePage';
import AvailabilityPage from './pages/stylist/AvailabilityPage';
import StylistAppointmentsPage from './pages/stylist/AppointmentsPage';
import StylistProfilePage from './pages/stylist/ProfilePage';
import StylistEditProfilePage from './pages/stylist/EditProfilePage';
import PortfolioPage from './pages/stylist/PortfolioPage';
import AddPortfolioItemPage from './pages/stylist/AddPortfolioItemPage';
import DownPaymentPolicyPage from './pages/stylist/DownPaymentPolicyPage';
import ReviewAddOnsPage from './pages/stylist/ReviewAddOnsPage';
import PromotionsPage from './pages/stylist/PromotionsPage';

// Providers
import { NotificationProvider } from './context/NotificationContext';
import { UIProvider } from './context/UIContext';
import { AffiliateProvider } from './context/AffiliateContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import { IdleLogoutProvider } from './context/IdleLogoutContext';
import Snackbar from './components/common/Snackbar';
import StylistIdleLogoutTimer from './components/common/StylistIdleLogoutTimer';

// Consent UI
import CookieBanner from './components/consent/CookieBanner';
import CookiePreferencesModal from './components/consent/CookiePreferencesModal';
import { usePendingAffiliateCookie } from './hooks/usePendingAffiliateCookie';

// Admin role guard component
interface AdminRoleGuardProps {
  children: React.ReactNode;
  requiredRole?: 'supportAgent' | 'super_admin';
}

const AdminRoleGuard: React.FC<AdminRoleGuardProps> = ({ children, requiredRole }) => {
  const { user, status } = useAppSelector((state) => state.auth);
  
  // Wait for auth to initialize before checking
  if (status === 'loading' || status === 'idle') {
    return null; // Or a loading spinner
  }
  
  // Check if user is admin (check both userType and role for compatibility)
  const actualUserType = user?.userType || user?.role;
  if (!user || actualUserType !== 'admin') {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user has required admin role
  if (requiredRole) {
    const adminRole = user?.adminRole as string | undefined;
    const isSuperAdmin = adminRole === 'super_admin' || adminRole === 'superAdmin';
    if (adminRole !== requiredRole && !isSuperAdmin) {
      return <Navigate to="/login" replace />;
    }
  }
  
  return <>{children}</>;
};

// Protected route component
interface ProtectedRouteProps {
  element: React.ReactNode;
  userType?: 'client' | 'stylist' | 'admin';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element, userType }) => {
  const dispatch = useAppDispatch();
  const { token, user, isLoading, status } = useAppSelector((state) => state.auth);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const restoreAttemptedRef = useRef(false);
  
  // Track if app has finished initializing (only check once on mount)
  useEffect(() => {
    // Mark as initialized after a short delay to allow auth state to settle
    const timer = setTimeout(() => {
      setHasInitialized(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Access tokens are memory-only. Redux is the reactive source; getToken covers
  // the short window between cookie refresh and the fulfilled Redux action.
  const activeToken = token || getToken();
  
  // Debug logging
  useEffect(() => {
    console.log('[ProtectedRoute] Auth state:', {
      hasToken: !!token,
      hasActiveToken: !!activeToken,
      hasUser: !!user,
      isLoading,
      status,
      userType: user?.userType || user?.role,
      path: window.location.pathname
    });
  }, [token, activeToken, user, isLoading, status]);
  
  // If we have a token but no user, try to fetch user
  // This must be called before any conditional returns (Rules of Hooks)
  useEffect(() => {
    if (!isCheckingAuth) return;

    if (activeToken && user) {
      setIsCheckingAuth(false);
      return;
    }

    // A missing memory token may still have a valid HttpOnly refresh cookie.
    // getCurrentUser performs the cookie refresh before loading the profile.
    if (status !== 'loading' && !restoreAttemptedRef.current) {
      restoreAttemptedRef.current = true;
      dispatch(getCurrentUser())
        .unwrap()
        .catch((error) => {
          console.error('[ProtectedRoute] Failed to restore session:', error);
        })
        .finally(() => {
          setIsCheckingAuth(false);
        });
    }
  }, [activeToken, user, status, dispatch, isCheckingAuth]);
  
  // Session already restored: keep the authenticated tree mounted during
  // background profile refreshes so pages cannot remount-loop into 429s.
  if (shouldShowProtectedRouteSpinner({
    hasInitialized,
    isCheckingAuth,
    isLoading,
    status,
    hasToken: !!activeToken,
    hasUser: !!user,
  })) {
    console.log('[ProtectedRoute] Showing loading state - checking authentication...', { hasInitialized, isCheckingAuth, hasToken: !!activeToken, hasUser: !!user, status });
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div className="spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #C9A84C',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  // Session restoration finished without both an access token and profile.
  if (!activeToken || !user) {
    console.log('[ProtectedRoute] No authenticated session, redirecting to login');
    // Don't redirect if we're already on the login page
    const currentPath = window.location.pathname;
    if (currentPath === '/login' || currentPath === '/register') {
      return <>{element}</>;
    }
    return <Navigate to="/login" replace />;
  }
  
  // Only check user type after session restoration has loaded the profile.
  if (user) {
    // Get actual user type from either userType or role field
    const actualUserType = user.userType || user.role;
    
    // Check if email is verified - redirect to verification page if not
    // Skip email verification check for admin users (support agents, etc.)
    if (!user.emailVerified && actualUserType !== 'admin') {
      console.log('Email not verified, redirecting to verification page');
      return <Navigate to="/verify-email" state={{ email: user.email, name: user.name }} replace />;
    }
    
    if (userType && actualUserType !== userType) {
      // Redirect based on actual user type
      if (actualUserType === 'client') {
        return <Navigate to="/client/dashboard" replace />;
      } else if (actualUserType === 'stylist') {
        return <Navigate to="/stylist/dashboard" replace />;
      } else if (actualUserType === 'admin') {
        // Support agents go to support dashboard
        return <Navigate to="/admin/dashboard" replace />;
      } else {
        return <Navigate to="/admin/dashboard" replace />;
      }
    }
  }
  
  // Render the element even if user is not loaded yet (component will handle loading state)
  return <>{element}</>;
};

// Protected affiliate route component
const ProtectedAffiliateRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const affiliateToken = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
  const { token, user } = useAppSelector((state) => state.auth);
  
  // If user is logged in as client/stylist/admin, redirect them away
  if (token && user) {
    const actualUserType = user.userType || user.role;
    if (actualUserType === 'client') {
      return <Navigate to="/client/dashboard" replace />;
    } else if (actualUserType === 'stylist') {
      return <Navigate to="/stylist/dashboard" replace />;
    } else if (actualUserType === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
  }
  
  // If no affiliate token, redirect to affiliate login
  if (!affiliateToken) {
    return <Navigate to="/affiliate/login" replace />;
  }
  
  // For nested routes, we need to render the element which should contain an <Outlet />
  return <>{element}</>;
};

function App() {
  const dispatch = useAppDispatch();
  const { token, user, status } = useAppSelector((state) => state.auth);
  const [isInitializing, setIsInitializing] = useState(true);

  usePendingAffiliateCookie();

  // Initialize auth state on app mount
  useEffect(() => {
    const initializeAuth = async () => {
      const protectedPath = /^\/(admin|client|stylist)(\/|$)/.test(window.location.pathname)
        || window.location.pathname === '/dashboard';
      const guardedByProtectedRoute = /^\/(admin|client|stylist)(\/|$)/.test(
        window.location.pathname,
      );
      const hasSessionHint = !!getToken() || !!getCsrfToken();
      console.log('[App] Initializing cookie-backed auth session:', {
        protectedPath,
        hasSessionHint,
      });

      // A refresh token is HttpOnly and cannot be checked from JavaScript.
      // ProtectedRoute owns restoration for its route trees. App handles the
      // standalone /dashboard redirect and restores hinted sessions on public
      // routes without racing the route guard.
      if (
        !user &&
        status !== 'loading' &&
        status !== 'succeeded' &&
        (!guardedByProtectedRoute && (protectedPath || hasSessionHint))
      ) {
        console.log('[App] Restoring user session from refresh cookie...');
        try {
          await dispatch(getCurrentUser()).unwrap();
          console.log('[App] User session restored successfully');
        } catch (error: any) {
          const errorPayload = error?.payload || error;
          const isConnectionError = 
            errorPayload?.code === 'ERR_CONNECTION_REFUSED' ||
            errorPayload?.status === 0;
          
          if (isConnectionError) {
            console.warn('[App] Backend connection refused during initialization');
            // Clear tokens to prevent infinite loops
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('refreshToken');
          } else {
            console.error('[App] Failed to restore user session:', error);
            // Error handling: getCurrentUser thunk clears localStorage on 401
            // If token was cleared, state will be updated accordingly
          }
        }
      } else if (!protectedPath && !hasSessionHint) {
        console.log('[App] No session restoration needed for public route');
      } else if (user) {
        console.log('[App] User already loaded');
      }
      
      setIsInitializing(false);
    };

    initializeAuth();
    // Auth bootstrap intentionally runs once for the initial URL. ProtectedRoute
    // owns restoration for later navigation into protected route trees.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Handle token changes (e.g., after login)
  // Use a ref to track if we've already attempted to fetch and failed due to connection error
  const fetchAttemptedRef = useRef(false);
  const connectionErrorRef = useRef(false);

  useEffect(() => {
    // If we have a token but no user and not currently loading, fetch user
    // Skip if status is 'succeeded' as user should already be set from login
    // Also skip if we're still initializing to avoid duplicate calls
    // Skip if we've already attempted and got a connection error
    if (
      token && 
      !user && 
      status !== 'loading' && 
      status !== 'succeeded' && 
      !isInitializing &&
      !connectionErrorRef.current
    ) {
      console.log('Token exists but no user data, fetching current user');
      fetchAttemptedRef.current = true;
      
      dispatch(getCurrentUser())
        .then((result) => {
          // Success - reset flags
          fetchAttemptedRef.current = false;
          connectionErrorRef.current = false;
        })
        .catch((error: any) => {
          // Check if it's a connection error
          const errorPayload = error?.payload || error;
          const isConnectionError = 
            errorPayload?.code === 'ERR_CONNECTION_REFUSED' ||
            errorPayload?.status === 0 ||
            errorPayload?.message?.includes('ERR_CONNECTION_REFUSED') ||
            errorPayload?.message?.includes('Connection refused') ||
            errorPayload?.message?.includes('Network Error') ||
            error?.code === 'ECONNREFUSED' ||
            error?.code === 'ERR_NETWORK';
          
          if (isConnectionError) {
            console.warn('[App] Backend connection refused, stopping retry attempts');
            connectionErrorRef.current = true;
            // Clear token if backend is unavailable to prevent infinite loops
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('refreshToken');
            // Clear Redux state manually since logout action might not be available
            // The token clearing above will cause the token selector to return null
            // which will prevent further retry attempts
          } else {
            // Other errors (401, etc.) - reset flag to allow retry
            fetchAttemptedRef.current = false;
          }
        });
    }
  }, [token, user, status, isInitializing, dispatch]); // Include dispatch

  // Keep real-time socket connection in sync with auth token
  useEffect(() => {
    if (token) {
      realtimeService.init(token);
    } else {
      realtimeService.disconnect();
    }
    return () => {
      // Do not disconnect on every re-render; only on unmount or token going away
    };
  }, [token]);

  return (
    <FeatureFlagsProvider>
    <CookieConsentProvider>
      <NotificationProvider>
        <UIProvider>
          <IdleLogoutProvider>
          {/* StylistIdleLogoutTimer is inert for non-stylists and logged-out users */}
          <StylistIdleLogoutTimer />
          <div className="app-layout">
            <CookieBanner />
            <CookiePreferencesModal />
            <Routes>
            {/* Public Homepage - No login required */}
            <Route path="/" element={<NewHomePage />} />
            
            {/* Auth routes with AuthLayout */}
            <Route element={<AuthLayout />}>
              {/* Auth routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
            </Route>

            {/* Affiliate Auth routes */}
            <Route path="/affiliate/login" element={<AffiliateLoginPage />} />
            <Route path="/affiliate/register" element={<AffiliateRegisterPage />} />
            <Route path="/affiliate/verify-email/:token" element={<EmailVerificationPage />} />

            {/* Affiliate protected routes */}
            <Route path="/affiliate" element={
              <ProtectedAffiliateRoute element={
                <AffiliateProvider>
                  <AffiliateLayout />
                </AffiliateProvider>
              } />
            }>
              <Route path="dashboard" element={<AffiliateDashboardPage />} />
              <Route path="commissions" element={<AffiliateCommissionsPage />} />
              <Route path="payouts" element={<AffiliatePayoutsPage />} />
              <Route path="marketing-materials" element={<AffiliateMarketingMaterialsPage />} />
              <Route path="profile" element={<AffiliateProfilePage />} />
              <Route path="analytics" element={<AffiliateAnalyticsPage />} />
              <Route path="help" element={<AffiliateHelpPage />} />
            </Route>
            
            {/* Old Full Homepage (keeping for reference) */}
            <Route path="/home-old" element={<HomePage />} />
            
            {/* Referral tracking route - must be before other routes */}
            <Route path="/ref/:referralCode" element={<ReferralTrackingPage />} />

            {/* New public pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/provider-agreement" element={<ProviderAgreementPage />} />
            <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
            <Route path="/community-standards" element={<CommunityStandardsPage />} />
            <Route path="/affiliate-terms" element={<AffiliateTermsPage />} />

            {/*
              Public, crawlable marketplace surface — deliberately OUTSIDE every
              ProtectedRoute. Before this, the only stylist-facing pages
              (/client/browse and /client/stylist/:shortCode) sat behind the
              auth gate, so an anonymous visitor or a crawler was redirected to
              /login and nothing was indexable. These routes are new and
              additive; the authenticated equivalents are unchanged.

              Every one of them resolves for any US state, city and service in
              the real taxonomy. Whether a given URL is INDEXED is a separate
              decision made per page from real inventory — see
              src/seo/core/eligibility.js.
            */}
            {/*
              Guest discovery (App Store Guideline 5.1.1(v)): browse, stylist
              profile and date/time selection without an account. The pages are
              the same ones /client/* renders; sign-in is required before
              Review & Book and for favorites / messaging (see utils/guestAccess).
            */}
            <Route element={<GuestMarketplaceLayout />}>
              <Route path="/browse" element={<BrowseStylesPage />} />
              <Route path="/browse/:shortCode" element={<StylistDetailsPage />} />
              <Route path="/browse/:shortCode/book" element={<BookingPage />} />
            </Route>

            {/* Professional funnel: public info about joining LookBel; "Join LookBel"
                hands off to the existing stylist registration/onboarding. */}
            <Route path="/become-a-stylist" element={<BecomeStylistPage />} />

            <Route path="/stylists" element={<StylistDirectoryPage />} />
            <Route path="/stylists/:seoSlug" element={<PublicStylistProfilePage />} />
            <Route path="/services/:serviceSlug" element={<ServiceHubPage />} />
            <Route path="/locations" element={<LocationIndexPage />} />
            <Route path="/locations/:stateSlug" element={<StateLandingPage />} />
            <Route path="/locations/:stateSlug/:citySlug" element={<CityLandingPage />} />
            <Route
              path="/locations/:stateSlug/:citySlug/:serviceSlug"
              element={<CityServiceLandingPage />}
            />

          {/* Client routes */}
          <Route path="/client" element={
            <ProtectedRoute 
              element={<DashboardLayout userType="client" />} 
              userType="client" 
            />
          }>
            <Route path="dashboard" element={<ClientDashboardPage />} />
            <Route path="browse" element={<BrowseStylesPage />} />
            <Route path="stylist/:shortCode" element={<StylistDetailsPage />} />
            <Route path="booking" element={<BookingPage />} />
            <Route path="booking-success" element={<BookingSuccessPage />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="appointment/:shortCode" element={<AppointmentDetailsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="edit-profile" element={<EditProfilePage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="chat/:conversationId" element={<ChatPage />} />
          </Route>

          {/* Stylist routes */}
          <Route path="/stylist" element={
            <ProtectedRoute 
              element={<DashboardLayout userType="stylist" />} 
              userType="stylist" 
            />
          }>
            <Route path="dashboard" element={<StylistDashboardPage />} />
            <Route path="service-template" element={<ServiceTemplatePage />} />
            <Route path="addon-template" element={<AddOnTemplatePage />} />
            <Route path="edit-template" element={<EditTemplatePage />} />
            <Route path="services" element={<ServiceManagementPage />} />
            <Route path="service-management" element={<ServiceManagementPage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="availability" element={<AvailabilityPage />} />
            <Route path="appointments" element={<StylistAppointmentsPage />} />
            <Route path="appointment/:shortCode" element={<AppointmentDetailsPage />} />
            <Route path="appointment/:shortCode/review-addons" element={<ReviewAddOnsPage />} />
            <Route path="profile" element={<StylistProfilePage />} />
            <Route path="edit-profile" element={<StylistEditProfilePage />} />
            <Route path="portfolio" element={<PortfolioPage />} />
            <Route path="portfolio/add" element={<AddPortfolioItemPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="down-payment-policy" element={<DownPaymentPolicyPage />} />
            <Route path="promotions" element={<PromotionsPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="chat/:conversationId" element={<ChatPage />} />
          </Route>

              {/* Admin routes - Enhanced security with role checks */}
              <Route path="/admin" element={
                <ProtectedRoute 
                  element={<DashboardLayout userType="admin" />} 
                  userType="admin" 
                />
              }>
                <Route path="dashboard" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <SupportDashboardPage />
                  </AdminRoleGuard>
                } />
                <Route path="support" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <SupportPage />
                  </AdminRoleGuard>
                } />
                <Route path="chat/:conversationId" element={<ChatPage />} />
                <Route path="affiliates" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <AffiliateManagementPage />
                  </AdminRoleGuard>
                } />
                <Route path="payouts" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <PayoutManagementPage />
                  </AdminRoleGuard>
                } />
                <Route path="disputes" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <DisputeManagementPage />
                  </AdminRoleGuard>
                } />
                <Route path="audit-trails" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <AuditTrailsPage />
                  </AdminRoleGuard>
                } />
                <Route path="email-verification" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <AdminEmailVerificationPage />
                  </AdminRoleGuard>
                } />
                <Route path="stylist-approval" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <StylistApprovalPage />
                  </AdminRoleGuard>
                } />
                <Route path="quick-user-search" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <QuickUserSearchPage />
                  </AdminRoleGuard>
                } />
                <Route path="feature-gates" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <FeatureGatesPage />
                  </AdminRoleGuard>
                } />
                <Route path="system-health" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <SystemHealthPage />
                  </AdminRoleGuard>
                } />
                <Route path="action-center" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <AdminActionCenterPage />
                  </AdminRoleGuard>
                } />
                <Route path="account-deletions" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <AccountDeletionsPage />
                  </AdminRoleGuard>
                } />
                <Route path="account-deletions/:id" element={
                  <AdminRoleGuard requiredRole="supportAgent">
                    <AccountDeletionDetailPage />
                  </AdminRoleGuard>
                } />
              </Route>

          {/* Dashboard redirect based on authentication status */}
          <Route path="/dashboard" element={
            token ? (
              user ? (
                (() => {
                  // Get actual user type from either userType or role field
                  const actualUserType = user?.userType || user?.role;
                  
                  if (actualUserType === 'client') {
                    return <Navigate to="/client/dashboard" replace />;
                  } else if (actualUserType === 'stylist') {
                    return <Navigate to="/stylist/dashboard" replace />;
                  } else {
                    return <Navigate to="/admin/dashboard" replace />;
                  }
                })()
              ) : (
                // Show loading while fetching user
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  minHeight: '100vh',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <div className="spinner" style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #C9A84C',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              )
            ) : (
              <Navigate to="/login" replace />
            )
          } />

              {/* Catch all for undefined routes */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Snackbar />
          </div>
          </IdleLogoutProvider>
        </UIProvider>
      </NotificationProvider>
    </CookieConsentProvider>
    </FeatureFlagsProvider>
  );
}

export default App;
